import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, FormControl } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { IconArrowRight } from "../../../../../icons/Icon";
import { fetchDasboardDates } from "../../../../../services/dashBoardDatesSlice";
import { setKitchenParams } from "../../../../../services/kitchenParamsSlice";
import { fetchRegion } from "../../../../../services/regionSlice";
import { setSiteParams } from "../../../../../services/siteParamsSlice";
import { fetchSites } from "../../../../../services/siteSlice";
import { AppDispatch, RootState } from "../../../../../store/store";

import MultiSelectNew from "../../../../MultiSelectNew";
import SingleSelect from "../../../SingleSelect";
import styles from "./filterdata.module.scss";

interface FilterDataProps {
  handleClick: (open: boolean) => void;
  handleMenuChange: (expanded: boolean) => void;
}

const FilterData: FC<FilterDataProps> = ({ handleClick, handleMenuChange }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Redux slices
  const { sites } = useSelector((state: RootState) => state.site);
  const { dashboardDates } = useSelector((state: RootState) => state.dashBoardDates);
  const { regions } = useSelector((state: RootState) => state.region);

  // Local state for filter selections.
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedDateOption, setSelectedDateOption] = useState<string>("");
  const [selectedRegionOptions, setSelectedRegionOptions] = useState<string[]>([]);
  const [selectedSiteOptions, setSiteSelectedOptions] = useState<string[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const hasLoadedRegionsRef = useRef(false);
  const hasLoadedSitesRef = useRef(false);
  const isInitialMount = useRef(true);

  // -----------------------------
  // Build dropdown options
  // -----------------------------
  const dateOptions = useMemo(() => {
    if (!dashboardDates || dashboardDates.length === 0) return [];
    return dashboardDates.map((projectionDate: any) => {
      const start = new Date(projectionDate.projectionstartdate);
      const end = addDays(start, 6);
      return {
        label: `${format(start, "dd-MMM-yyyy")} to ${format(end, "dd-MMM-yyyy")}`,
        value: String(projectionDate.projectionstartdate)
      };
    });
  }, [dashboardDates]);

  const regionOptions = useMemo(() => {
    if (!regions || regions.length === 0) return [];
    return regions.map((reg: any) => ({
      label: reg.regionname,
      value: String(reg.regionid),
      code: String(reg.regionid),
      id: String(reg.regionid)
    }));
  }, [regions]);

  const filteredSites = useMemo(() => {
    const allSites = sites || [];
    const hasRegionMapping = allSites.some(
      (site: any) => site?.regionid !== undefined && site?.regionid !== null && site?.regionid !== ''
    );

    if (!selectedRegionOptions.length || !hasRegionMapping) return allSites;

    const selectedRegionSet = new Set(selectedRegionOptions);
    return allSites.filter((site: any) => selectedRegionSet.has(String(site?.regionid)));
  }, [sites, selectedRegionOptions]);

  const siteOptions = useMemo(() => {
    if (!filteredSites || filteredSites.length === 0) return [];
    return filteredSites.map((site: any) => ({
      id: site.sitecode,
      label: site.sitealias || site.sitename,
      code: site.sitecode
    }));
  }, [filteredSites]);

  const stringSites = useMemo(() => {
    if (!sites) return [];
    return sites.map((site: any) => site.sitecode);
  }, [sites]);

  // -----------------------------
  // Effects: Setup & API dispatches
  // -----------------------------
  // Retrieve userId from localStorage on mount.
  useEffect(() => {
    const userInfoStr = localStorage.getItem("userInfo");
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo?.userdetails?.userid) {
          setUserId(userInfo.userdetails.userid);
        }
      } catch (err) {
        console.error("Failed to parse userInfo from localStorage:", err);
      }
    }
  }, []);

  // When userId is available, fetch region and dashboard dates.

  useEffect(() => {
    if (!userId) return;
    if (!hasLoadedRegionsRef.current && regions.length === 0) {
      hasLoadedRegionsRef.current = true;
      dispatch(fetchRegion(userId));
    }
    if (!hasLoadedSitesRef.current && sites.length === 0) {
      hasLoadedSitesRef.current = true;
      dispatch(fetchSites(userId));
    }
  }, [userId, regions.length, sites.length, dispatch]);

  let modulecode = location.pathname === "/sitemanagerdashboard" ? "03" : "02";
  useEffect(() => {
    if (!userId) return;
    dispatch(fetchDasboardDates({ userId, modulecode }));
  }, [userId, modulecode, dispatch]);


  // -----------------------------
  // Handler: Update filter values via a single function.
  // -----------------------------
  const updateFilter = useCallback(
    (type: "date" | "region", value: string | string[]) => {
      if (type === "region") {
        // Normalize to an array so single (auto-select) and multi (MultiSelect) both work.
        const regionList = Array.isArray(value) ? value : value ? [value] : [];
        setSelectedRegionOptions(regionList);
        // Clear any previously selected sites when region changes.
        setSiteSelectedOptions([]);
        // Ensure that the dashboard details are closed.
        handleClick(false);
      } else if (type === "date") {
        setSelectedDateOption(value as string);
        // Also close dashboard details on date change.
        handleClick(false);
      }
    },
    [userId, dispatch, handleClick]
  );

  useEffect(() => {
    if (regionOptions.length === 1 && selectedRegionOptions.length === 0) {
      updateFilter('region', regionOptions[0].value);
    }
  }, [regionOptions, selectedRegionOptions.length])
  
  // -----------------------------
  // Effects: Validate and clear invalid date selection
  // -----------------------------
  useEffect(() => {
    if (isInitialMount.current && dateOptions.length > 0) {
      isInitialMount.current = false;
    }
    
    if (dateOptions.length === 0) {
      // Clear selection when no options available
      if (selectedDateOption) {
        setSelectedDateOption("");
      }
      return;
    }
    
    // Validate current selection
    if (selectedDateOption) {
      const isValid = dateOptions.some(option => option.value === selectedDateOption);
      if (!isValid) {
        setSelectedDateOption("");
      }
    }
  }, [dateOptions, selectedDateOption]);
  
  // -----------------------------
  // Effects: Auto-select single options and enable submit.
  // -----------------------------
  useEffect(() => {
    if (dateOptions.length === 1 && !selectedDateOption) {
      setSelectedDateOption(dateOptions[0].value);
    }
    if (regionOptions.length === 1 && selectedRegionOptions.length === 0) {
      setSelectedRegionOptions([regionOptions[0].value]);
    }
    if (siteOptions.length === 1 && selectedSiteOptions.length === 0) {
      setSiteSelectedOptions([siteOptions[0].code]);
    }
  }, [dateOptions, regionOptions, siteOptions]);

  // useEffect(() => {
  //   setDisableSubmit(!(selectedRegionOption && selectedDateOption));
  // }, [selectedRegionOption, selectedDateOption]);

  useEffect(() => {
    // Region is optional; only the date is required to enable submit.
    setDisableSubmit(!selectedDateOption);
  }, [selectedDateOption]);

  // -----------------------------
  // Handler: Final submission.
  // -----------------------------
  const handleSubmit = useCallback(() => {
    if (!userId || !selectedDateOption) return;

    const fromdate = selectedDateOption;
    const todate = format(addDays(new Date(selectedDateOption), 6), "yyyy-MM-dd'T'HH:mm:ssXXX");

    const visibleSites = selectedRegionOptions.length > 0 ? filteredSites : sites;
    const visibleSiteCodes = visibleSites.map((site: any) => site.sitecode);
    const isAllSites =
      visibleSiteCodes.length === selectedSiteOptions.length || selectedSiteOptions.length === 0;
    const siteidlist = isAllSites ? visibleSiteCodes : selectedSiteOptions;

    // All regions are considered selected when every region is chosen, or when
    // none is chosen (region is optional). Mirrors the isAllSites logic.
  

    const payload = {
      userid: userId,
      fromdate,
      todate,
      siteidlist,
      isallsite: isAllSites,
      // Always send regionid as an array of strings, e.g. ["30","4"].
      regionid: selectedRegionOptions.map(String),
      // true when all regions are selected (or none, since region is optional)
      //isallregion: isAllRegions
    };

    // Dispatch the payload to the appropriate Redux slice.
    if (location.pathname === "/sitemanagerdashboard") {
      dispatch(setSiteParams(payload));
    } else {
      dispatch(setKitchenParams(payload));
    }

    // Finally, open the dashboard details.
    handleClick(true);
    // Also, ensure the parent's menu is expanded.
    handleMenuChange(true);
  }, [
    userId,
    selectedDateOption,
    sites,
    filteredSites,
    siteOptions,
    selectedSiteOptions,
    selectedRegionOptions,
    regionOptions,
    dispatch,
    location.pathname,
    handleClick,
    handleMenuChange
  ]);

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl fullWidth>
        <div className="flex justify-content-between align-items-end">
          <div className={`${styles.leftBox} flex justify-content-between align-items-end`}>
            {/* Menu Period */}
            <div className={`flex flex-column mr-24 ${styles.multiSelect}`}>
              <SingleSelect
                label="Menu Period"
                options={dateOptions}
                value={
                  !dateOptions || dateOptions.length === 0 || !selectedDateOption
                    ? ""
                    : dateOptions.some(opt => opt.value === selectedDateOption)
                    ? selectedDateOption
                    : ""
                }
                onChange={(event) =>
                  updateFilter("date", String(event.target.value))
                }
                placeholder="Select Date range"
                disabled={dateOptions.length < 2}
              />
            </div>
            {/* Region */}
            <div className={`flex flex-column mr-24 ${styles.multiSelect}`}>
              <MultiSelectNew
                label="Region"
                options={regionOptions}
                selectedValues={selectedRegionOptions}
                onApply={(selected) => updateFilter("region", selected)}
                placeholder="Select Region"
                showLabelOnly={true}
                selectAllLabel="All Region"
                hideSelectAllOnSearch={true}
                disabled={regionOptions.length === 1}
              />
            </div>
            {/* Site (MultiSelect) */}
            <div className={`flex flex-column ${styles.multiSelect}`}>
              <MultiSelectNew
                options={siteOptions}
                label="Select Site"
                disabled={siteOptions.length === 0}
                selectedValues={selectedSiteOptions}
                onApply={(value) => setSiteSelectedOptions(value)}
                placeholder="Select Site"
                selectAllLabel="All Site"
                showLabelOnly={true}
                hideSelectAllOnSearch={true}
              />
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={disableSubmit}
            className={`primary-btn ${styles.primaryBtn}`}
          >
            Go <IconArrowRight />
          </Button>
        </div>
      </FormControl>
    </LocalizationProvider>
  );
};

export default memo(FilterData);
